import { CheckIcon, ChevronDownIcon } from '@/components/Icons';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import clsx from 'clsx';
import { withClassName } from 'hocs/withClassName';
import { forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

/**
 * DropdownMenu is a primitive component that allows you to create a dropdown
 * list of options that can be selected. Use this for a control which is clicked
 * to show a menu which consists only of selectable items in a list - not more
 * complex content.
 *
 * The basic structure of a DropdownMenu is:
 *
 * <DropdownMenuRoot>
 *  <DropdownMenuButton>
 *    Some text
 *   </DropdownMenuButton>
 *   <DropdownMenuContent>
 *     <DropdownMenuItem onSelect={handleSelect}>Item 1</DropdownMenuItem>
 *     <DropdownMenuItem onSelect={handleSelect}>Item 2</DropdownMenuItem>
 *     <DropdownMenuCheckboxItem checked={checked} onCheckedChange={handleCheckedChange}>
 *       Item 3
 *     </DropdownMenuCheckboxItem>
 *   </DropdownMenuContent>
 * </DropdownMenuRoot>
 *
 * For further customization of individual elements, more low-level components
 * are exposed:
 *
 * - DropdownMenuButtonRoot: the root element of the button, allowing you to wrap complex contents or utilize asChild to attach to a child component
 * - DropdownMenuButtonIcon: the chevron icon that indicates the dropdown is open. automatically rotates.
 */
export const DropdownMenuRoot = DropdownMenu.Root;
export const DropdownMenuButtonRoot = withClassName(
  DropdownMenu.Trigger,
  'group rounded-md  flex flex-row gap-1 items-center',
  'hover:border-content-tertiary active:border-content-primary focus:bg-transparent focus:outline-none focus-visible:ring-1',
);
export const DropdownMenuButton = forwardRef<
  HTMLButtonElement,
  Omit<DropdownMenu.DropdownMenuTriggerProps, 'asChild'>
>(function DropdownMenuButton({ children, ...props }, ref) {
  return (
    <DropdownMenuButtonRoot {...props} ref={ref}>
      {children}
      <DropdownMenuButtonIcon />
    </DropdownMenuButtonRoot>
  );
});
export const DropdownMenuContent = forwardRef<
  HTMLDivElement,
  DropdownMenu.DropdownMenuContentProps
>(function DropdownMenuContent({ className, ...props }, ref) {
  return (
    <DropdownMenu.Portal>
      <DropdownMenu.Content
        {...props}
        ref={ref}
        className={twMerge(
          'focus:outline-none mt-2 divide-y divide-subtle rounded-md bg-surface shadow-lg ring-1 ring-black ring-opacity-5 flex flex-col origin-radix-popover radix-state-open:animate-popoverIn radix-state-closed:animate-popoverOut z-50',
          'min-w-[var(--radix-dropdown-menu-trigger-width)] max-h-[var(--radix-dropdown-menu-content-available-height)]',
          className,
        )}
      />
    </DropdownMenu.Portal>
  );
});
export const DropdownMenuButtonIcon = withClassName(
  ChevronDownIcon,
  'justify-self-end h-[12px] w-[12px] group-radix-state-open:rotate-180 transition-transform',
);
export const DropdownMenuItem = withClassName(
  DropdownMenu.Item,
  'flex w-full rounded-md py-2 px-4 text-sm font-normal radix-highlighted:bg-overlay-hover cursor-pointer items-center gap-2',
);
export const DropdownMenuCheckboxItem = forwardRef<
  HTMLDivElement,
  DropdownMenu.DropdownMenuCheckboxItemProps
>(function DropdownMenuCheckboxItem({ className, children, ...props }, ref) {
  return (
    <DropdownMenu.CheckboxItem
      className={twMerge(
        'group flex w-full rounded-md py-2 px-4 text-sm font-normal radix-highlighted:bg-overlay-hover cursor-pointer items-center gap-2',
        'radix-disabled:opacity-25 radix-disabled:pointer-events-none focus:outline-none focus-visible:ring-1 focus-visible:relative',
        className,
      )}
      {...props}
      ref={ref}
    >
      <div
        className={clsx(
          'flex h-4 w-4 items-center justify-center rounded-sm border',
          'group-radix-state-checked:border-btn-action-bg group-radix-state-checked:bg-btn-action-bg dark:group-radix-state-checked:border-accent-blue dark:group-radix-state-checked:bg-accent-blue',
          'group-radix-state-unchecked:border-strong group-radix-state-unchecked:bg-surface',
          'active:group-radix-state-unchecked:border-btn-action-bg',
        )}
      >
        <DropdownMenu.ItemIndicator>
          <CheckIcon className="h-3 w-3 self-center text-white dark:text-surface" />
        </DropdownMenu.ItemIndicator>
      </div>
      {children}
    </DropdownMenu.CheckboxItem>
  );
});

export const DropdownMenuSeparator = withClassName(
  DropdownMenu.Separator,
  'h-px my-1 bg-border-subtle',
);

export const DropdownMenuSub = DropdownMenu.Sub;
export const DropdownMenuSubTrigger = withClassName(
  DropdownMenu.SubTrigger,
  'flex w-full rounded-md py-2 px-4 text-sm font-normal radix-highlighted:bg-overlay-hover cursor-pointer items-center gap-2 outline-none',
);
export const DropdownMenuSubContent = forwardRef<
  HTMLDivElement,
  DropdownMenu.DropdownMenuSubContentProps
>(function DropdownMenuSubContent({ className, ...props }, ref) {
  return (
    <DropdownMenu.Portal>
      <DropdownMenu.SubContent
        {...props}
        ref={ref}
        className={twMerge(
          'focus:outline-none rounded-md bg-surface shadow-lg ring-1 ring-black ring-opacity-5 flex flex-col origin-radix-popover radix-state-open:animate-popoverIn radix-state-closed:animate-popoverOut z-50 p-1',
          className,
        )}
      />
    </DropdownMenu.Portal>
  );
});
export const DropdownMenuLabel = withClassName(
  DropdownMenu.Label,
  'flex w-full py-2 px-4 text-sm font-normal text-content-tertiary items-center gap-2',
);

// TODO: support other DropdownMenu primitives like Group, different item types
// SEE: https://www.radix-ui.com/primitives/docs/components/dropdown-menu
